/**
 * Ажлын Групп feed API (/job-group).
 *
 * Jobseekers post what they are looking for; anyone can read the feed. Reading works
 * signed-out, so the list call skips auth — the server answers "liked"/"isMine" only
 * when a token happens to be present.
 */

import http from '../client';
import { ENDPOINTS } from '../config';

/**
 * One matched job -> the shape the feed's job card already renders.
 *
 * The agent picks vacancies and the server resolves them against live ads on every read,
 * so these rows arrive with the vacancy's current salary and address rather than a
 * snapshot from whenever the agent looked. Field names follow the job list's own mapping
 * (`useJobs`), so the card component needs no branch for where a job came from.
 */
export const toSuggestedJob = (row) => ({
  id: row.id,
  name: row.name,
  companyName: row.companyName || "",
  // Same field the job list maps to `logo`.
  logo: row.companyPhoto || null,
  minSalary: row.minSalary != null ? Number(row.minSalary) : null,
  maxSalary: row.maxSalary != null ? Number(row.maxSalary) : null,
  salaryText: row.salaryText || null,
  salaryType: row.salaryType || null,
  employmentType: row.employmentType || null,
  // The card shows one line for where the job is; the branch address is the fuller of
  // the two, and the district is the fallback when a branch has no address on file.
  location: row.branchAddress || row.district || "Mongolia",
  district: row.district || null,
  displayDate: row.postedDate || null,
});

/**
 * The agent's answer for one post.
 *
 * `status` is the whole contract: READY means `jobs` is the answer, PENDING means it is
 * still being worked out, and anything else means the client should fall back to matching
 * the post itself.
 */
export const toSuggestions = (dto) => {
  if (!dto) return null;
  return {
    status: dto.status || null,
    jobs: (dto.jobs || []).map(toSuggestedJob),
    signals: dto.signals || [],
    district: dto.district || null,
    noMatch: !!dto.noMatch,
    // SEEKING, OFFERING or UNCLEAR. An employer advertising a job gets told so rather
    // than being shown a list of jobs they were never asking for, and an UNCLEAR post
    // ("Hi", a test) is answered with no jobs at all rather than a guess.
    postKind: dto.postKind || null,
    suggestedAt: dto.suggestedAt || null,
  };
};

/**
 * Server post -> the shape the /job-group page renders.
 *
 * `intent` comes back exactly as the parser produced it — `{ filters, signals,
 * underage }` — because the feed drives its job matching off that whole object, not
 * off the individual columns.
 */
export const toFeedPost = (dto) => {
  let intent = null;
  if (dto.intentJson) {
    try {
      intent = JSON.parse(dto.intentJson);
    } catch {
      // A post whose stored intent won't parse still renders; it just shows no
      // matched jobs, which beats blanking the whole feed.
      intent = null;
    }
  }

  return {
    id: dto.id,
    text: dto.content,
    anonymous: !!dto.isAnonymous,
    authorName: dto.authorName,
    authorPhotoUrl: dto.authorPhotoUrl,
    // Null for an anonymous post — the server withholds it the same way it withholds
    // authorName/authorPhotoUrl, so anonymity can't be undone by following this id.
    authorCandidateId: dto.authorCandidateId ?? null,
    isMine: !!dto.isMine,
    createdAt: dto.createdDate,
    feeling: dto.feeling,
    hidden: false,
    liked: !!dto.liked,
    likeCount: dto.likeCount || 0,
    commentCount: dto.commentCount || 0,
    // The first few replies ride along with the feed so a thread shows without a
    // request per card; expanding fetches the rest.
    comments: (dto.comments || []).map(toFeedComment),
    intent,
    // Rides along with the feed, so a card knows what to show without a request of its own.
    suggestions: toSuggestions(dto.suggestions),
  };
};

/**
 * Parser output -> what the API stores.
 *
 * The whole object is kept as JSON so nothing is lost, plus the few fields worth
 * querying are projected into real columns — a JSON blob can't be filtered on.
 */
export const toIntentPayload = (intent) => ({
  // Was intent.filters.categoryIds[0]. Those ids were guesses at the backend's category
  // table and have been removed from the parser — see CandidateSearchDemo.jsx. Null is
  // honest; no caller filters the feed by this column yet.
  industrySubId: null,
  // The trade the post is about. This used to take the last of `signals`, which is
  // whichever label happened to be pushed last — the district when one was named, and
  // "Туршлагатай" once experience was read. The parser now says outright what the post
  // is about, so guessing from the display labels is no longer necessary.
  jobTitle: intent?.searchQueries?.[0] ?? intent?.filters?.searchQuery ?? null,
  salaryMin: intent?.filters?.minSalary ?? null,
  // The place lives beside the filters, not inside them: the jobs endpoint has no
  // location parameter, so it is applied to the results instead of the query. The
  // older nesting is still read so posts saved before that change keep their district.
  district: intent?.district ?? intent?.filters?.district ?? null,
  intentJson: intent ? JSON.stringify(intent) : null,
});

/**
 * A page of the feed.
 * @param {{page?: number, size?: number, country?: string, industrySubId?: number}} opts
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getGroupPosts = async ({ page = 0, size = 20, country, industrySubId } = {}) => {
  const params = { page, size };
  if (country) params.country = country;
  if (industrySubId) params.industrySubId = industrySubId;

  try {
    // Not skipAuth: signed-in readers get `liked` and `isMine` filled in, and the
    // endpoint is public either way.
    return await http.get(ENDPOINTS.GROUP_POSTS.LIST, params);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Publish a post. `intent` is what the client parsed out of the text
 * (industrySubId / jobTitle / salaryMin / district) — sent so the feed can be
 * filtered without re-parsing free text on every read.
 */
export const createGroupPost = async ({ content, isAnonymous, feeling, intent = {}, country }) => {
  try {
    return await http.post(ENDPOINTS.GROUP_POSTS.CREATE, {
      content,
      isAnonymous: !!isAnonymous,
      feeling: feeling || null,
      country: country || null,
      ...intent,
    });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * One post's matched jobs, for polling a post the agent is still answering. The feed
 * already inlines the same object, so this is only needed after composing.
 */
export const getPostSuggestions = async (id) => {
  try {
    return await http.get(ENDPOINTS.GROUP_POSTS.SUGGESTIONS(id));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Remove your own post. */
export const deleteGroupPost = async (id) => {
  try {
    return await http.delete(ENDPOINTS.GROUP_POSTS.DELETE(id));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Like or unlike. Returns the post as the server now sees it. */
export const toggleGroupPostLike = async (id) => {
  try {
    return await http.post(ENDPOINTS.GROUP_POSTS.LIKE(id));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/* -------------------------------------------------------------------------
 * Replies
 *
 * Readable by anyone, writable by anyone signed in — a jobseeker, the person whose post
 * it is, or an employer. The page's "only jobseekers may post" guard governs creating a
 * post, not answering one.
 * ---------------------------------------------------------------------- */

/** Server comment -> the shape the thread renders. */
export const toFeedComment = (dto) => ({
  id: dto.id,
  postId: dto.postId,
  parentId: dto.parentCommentId ?? null,
  text: dto.content,
  authorName: dto.authorName,
  authorPhotoUrl: dto.authorPhotoUrl,
  /** "COMPANY" replies are badged — an employer answering is the signal that matters. */
  isCompany: dto.authorKind === "COMPANY",
  companyId: dto.authorCompanyId ?? null,
  // Null for a company-authored reply — mirrors companyId's split.
  authorCandidateId: dto.authorCandidateId ?? null,
  isMine: !!dto.isMine,
  isPostAuthor: !!dto.isPostAuthor,
  createdAt: dto.createdDate,
});

/**
 * One candidate's own posts — the Ажлын групп section on their public profile.
 *
 * Anonymous posts never come back: the server withholds them rather than the client
 * filtering, so a post someone chose to hide their name on cannot be attached to that name
 * by anything reading this.
 */
export const getPostsByCandidate = async (candidateId, limit = 20) => {
  try {
    const res = await http.get(ENDPOINTS.GROUP_POSTS.BY_CANDIDATE(candidateId), { limit });
    const rows = res.data?.data || res.data;
    return res.success && Array.isArray(rows)
      ? { success: true, data: rows.map(toFeedPost) }
      : { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** The full thread for one post. */
export const getComments = async (postId) => {
  try {
    const res = await http.get(ENDPOINTS.GROUP_POSTS.COMMENTS(postId));
    return res.success && Array.isArray(res.data)
      ? { success: true, data: res.data.map(toFeedComment) }
      : { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Post a reply. `parentId` replies to a comment; the server re-parents a reply-to-a-reply
 * to its root, so the thread stays one level deep whatever is sent.
 */
export const addComment = async (postId, { content, parentId = null }) => {
  try {
    const res = await http.post(ENDPOINTS.GROUP_POSTS.COMMENTS(postId), {
      content,
      parentCommentId: parentId,
    });
    return res.success && res.data
      ? { success: true, data: toFeedComment(res.data) }
      : { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Remove your own reply. */
export const deleteComment = async (postId, commentId) => {
  try {
    return await http.delete(ENDPOINTS.GROUP_POSTS.COMMENT(postId, commentId));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const groupPostsService = {
  toFeedPost,
  toFeedComment,
  getComments,
  addComment,
  deleteComment,
  toIntentPayload,
  getGroupPosts,
  createGroupPost,
  deleteGroupPost,
  toggleGroupPostLike,
};

export default groupPostsService;
